import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, apiFetch } from '../../config/api';
import { useMessages } from '../Messages';
import { showApiError } from '../../utils/errorUtils';
import UOMSelect from '../Form/UOMSelect';

const ShiftHandoffModal = ({ isOpen, onClose, taskId, taskType = 'STANDARD', onHandoffComplete, taskContext }) => {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('operator');
  const { showSuccess, showError } = useMessages();
  const [loading, setLoading] = useState(false);
  const [quantity, setQuantity] = useState('');
  const [uom, setUom] = useState('PCS');
  const [wasteKg, setWasteKg] = useState('');
  const [notes, setNotes] = useState('');

  // For PRINTING: the rolls no report has counted yet; the backend records their rolls, meters and kg
  const [rolls, setRolls] = useState([]);
  const [loadingRolls, setLoadingRolls] = useState(false);

  const isPrinting = taskType === 'PRINTING';

  useEffect(() => {
    if (isOpen && isPrinting && taskId) {
      setLoadingRolls(true);
      apiFetch(getApiUrl(`/api/production-tasks/tasks/${taskId}/rolls`))
        .then(res => res.ok ? res.json() : [])
        .then(data => setRolls(Array.isArray(data) ? data.filter(r => !r.completion_log_id) : []))
        .catch(() => setRolls([]))
        .finally(() => setLoadingRolls(false));
    }
  }, [isOpen, isPrinting, taskId]);

  const computedWaste = isPrinting
    ? rolls.reduce((s, r) => s + (r.waste_weight_kg || 0), 0).toFixed(2)
    : null;
  const computedMeters = rolls.reduce((s, r) => s + (parseFloat(r.final_meters) || 0), 0).toFixed(1);
  const computedKg = rolls.reduce((s, r) => s + (parseFloat(r.final_weight_kg) || 0), 0).toFixed(2);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 0 is valid: what was already registered with "Registrar Producción" is not entered again
    if (!isPrinting && (quantity === '' || !(parseFloat(quantity) >= 0))) {
      showError(t('errors.generic.error'), t('finalizarTurno.validationQuantity'));
      return;
    }

    setLoading(true);
    try {
      const payload = isPrinting
        ? {
            quantity_produced: rolls.length,
            uom: 'ROLL',
            waste_kg: parseFloat(computedWaste) || 0,
            notes: notes || null,
          }
        : {
            quantity_produced: parseFloat(quantity),
            uom: uom,
            waste_kg: parseFloat(wasteKg) || 0,
            notes: notes || null,
          };

      const response = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${taskId}/shift-handoff`), {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        showSuccess(t('finalizarTurno.title'), t('finalizarTurno.success'));
        handleClose();
        onHandoffComplete();
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error during shift handoff:', error);
      showError(t('finalizarTurno.title'), t('finalizarTurno.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setQuantity('');
      setUom('PCS');
      setWasteKg('');
      setNotes('');
      setRolls([]);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="dialog-overlay" onClick={handleClose}>
      <div className="dialog-content" onClick={e => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>{t('finalizarTurno.title')}</h2>
          <button className="dialog-close" onClick={handleClose} disabled={loading}>×</button>
        </div>
        {taskContext && (taskContext.customer_name || taskContext.po_number) && (
          <div style={{ padding: '0.4rem 1.25rem', background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)', fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'flex', gap: '1rem' }}>
            {taskContext.customer_name && <span><strong>{t('common:ui.customer')}:</strong> {taskContext.customer_name}</span>}
            {taskContext.po_number && <span><strong>{t('labels.order')}:</strong> {taskContext.po_number}</span>}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="dialog-body">
            {isPrinting ? (
              <div className="handoff-summary">
                {loadingRolls ? (
                  <p style={{ color: '#6c757d' }}>{t('finalizarTurno.loadingRolls')}</p>
                ) : (
                  <div className="printing-auto-summary">
                    <p style={{ marginBottom: 12, color: '#2c3e50' }}>
                      {t('finalizarTurno.autoSummaryText')}
                    </p>
                    <div className="summary-chips">
                      <span className="summary-chip">
                        {t('finalizarTurno.rollsLabel')} <strong>{rolls.length}</strong>
                      </span>
                      <span className="summary-chip">
                        {t('finalizarTurno.metersLabel')} <strong>{computedMeters} m</strong>
                      </span>
                      <span className="summary-chip">
                        {t('finalizarTurno.kgLabel')} <strong>{computedKg} kg</strong>
                      </span>
                      <span className="summary-chip">
                        {t('finalizarTurno.wasteLabel')} <strong>{computedWaste} kg</strong>
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label>{t('finalizarTurno.quantityProduced')}</label>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    placeholder={t('finalizarTurno.quantityPlaceholder')}
                    min="0"
                    step="0.001"
                    required
                    disabled={loading}
                    autoFocus
                  />
                </div>

                <div className="form-group">
                  <label>{t('finalizarTurno.unitOfMeasure')}</label>
                  <UOMSelect
                    value={uom}
                    onChange={(value) => setUom(value)}
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label>{t('finalizarTurno.waste')}</label>
                  <input
                    type="number"
                    value={wasteKg}
                    onChange={(e) => setWasteKg(e.target.value)}
                    placeholder={`${t('placeholders.wasteQuantity')} kg`}
                    min="0"
                    step="0.001"
                    disabled={loading}
                  />
                </div>
              </>
            )}

            <div className="form-group">
              <label>{t('placeholders.notes')}</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('finalizarTurno.notesPlaceholder')}
                rows="3"
                disabled={loading}
              />
            </div>
          </div>

          <div className="dialog-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleClose}
              disabled={loading}
            >
              {t('buttons.cancel')}
            </button>
            <button
              type="submit"
              className="btn-warning"
              disabled={loading || (!isPrinting && !quantity)}
            >
              {loading ? t('finalizarTurno.processing') : t('finalizarTurno.confirm')}
            </button>
          </div>
        </form>

        <style>{`
          .handoff-summary {
            margin-bottom: 12px;
          }
          .printing-auto-summary {
            background: #e8f4fd;
            border: 1px solid #b8d8f0;
            border-radius: 6px;
            padding: 14px 16px;
          }
          .summary-chips {
            display: flex;
            gap: 16px;
            flex-wrap: wrap;
          }
          .summary-chip {
            background: white;
            border: 1px solid #b8d8f0;
            border-radius: 20px;
            padding: 6px 14px;
            font-size: 14px;
            color: #2c3e50;
          }
        `}</style>
      </div>
    </div>
  );
};

export default ShiftHandoffModal;
